<template>
  <div class="view games-page">
    <h1 class="page-title">{{ $t('admin.games.title') }}</h1>

    <!-- Game selector -->
    <div class="game-tabs">
      <button
        v-for="game in games"
        :key="game.id"
        class="game-tab"
        :class="{ active: selectedGame === game.id }"
        @click="selectedGame = game.id"
      >
        {{ game.label }}
      </button>
    </div>

    <!-- Game overview bar (shown for all games) -->
    <div class="game-overview-bar">
      <div class="overview-left">
        <span class="overview-title">{{ selectedGameLabel }}</span>
        <span class="overview-slug">{{ selectedGame }}</span>
        <span
          class="status-badge"
          :class="currentState.is_enabled ? 'badge-enabled' : 'badge-disabled'"
        >
          {{ currentState.is_enabled ? $t('admin.games.enabled') : $t('admin.games.disabled') }}
        </span>
        <span v-if="currentState.is_hot" class="status-badge badge-hot">{{ $t('admin.games.hot') }}</span>
        <span v-if="currentState.is_new" class="status-badge badge-new">{{ $t('admin.games.new') }}</span>
      </div>

      <div class="overview-right">
        <!-- Display order -->
        <label class="order-label">
          {{ $t('admin.games.order_label') }}
          <input
            v-model.number="currentState.display_order"
            type="number"
            min="0"
            class="order-input"
            @change="saveOrder"
          />
        </label>

        <!-- Category dropdown -->
        <label class="category-label">
          {{ $t('admin.games.category_label') }}
          <select v-model="currentState.category" class="category-select" @change="saveCategory">
            <option value="">{{ $t('admin.games.category_select') }}</option>
            <option value="table">{{ $t('admin.games.category_table') }}</option>
            <option value="slots">{{ $t('admin.games.category_slots') }}</option>
            <option value="instant">{{ $t('admin.games.category_instant') }}</option>
            <option value="crash">{{ $t('admin.games.category_crash') }}</option>
            <option value="card">{{ $t('admin.games.category_card') }}</option>
          </select>
        </label>

        <!-- Enable / disable toggle -->
        <div class="toggle-wrapper">
          <span class="toggle-text">{{ currentState.is_enabled ? $t('admin.games.enabled') : $t('admin.games.disabled') }}</span>
          <button
            class="toggle-switch"
            :class="{ 'toggle-on': currentState.is_enabled }"
            :disabled="toggling"
            @click="toggleEnabled"
            type="button"
          >
            <span class="toggle-knob" />
          </button>
        </div>
      </div>
    </div>

    <!-- Metadata editor (collapsible) -->
    <div class="metadata-section">
      <button class="metadata-toggle-btn" type="button" @click="metaOpen = !metaOpen">
        <span>{{ $t('admin.games.edit_metadata') }}</span>
        <span class="chevron" :class="{ open: metaOpen }">▾</span>
      </button>

      <div v-if="metaOpen" class="metadata-body">
        <div class="meta-grid">
          <label class="form-label">
            {{ $t('admin.games.meta_title') }}
            <input v-model="metaForm.title" type="text" class="form-input" placeholder="Game title" />
          </label>

          <label class="form-label">
            {{ $t('admin.games.meta_rtp') }}
            <input v-model.number="metaForm.rtp" type="number" step="0.01" class="form-input" placeholder="e.g. 97.0" />
          </label>

          <label class="form-label">
            {{ $t('admin.games.meta_rules_url') }}
            <input v-model="metaForm.rules_url" type="text" class="form-input" placeholder="https://..." />
          </label>

          <label class="form-label">
            {{ $t('admin.games.meta_thumbnail_url') }}
            <input v-model="metaForm.thumbnail_url" type="text" class="form-input" placeholder="https://..." />
          </label>

          <label class="form-label meta-full">
            {{ $t('admin.games.meta_description') }}
            <textarea v-model="metaForm.description" class="form-textarea" rows="3" placeholder="Short game description" />
          </label>
        </div>

        <div class="meta-actions">
          <button type="button" class="btn btn-primary" :disabled="savingMeta" @click="saveMetadata">
            {{ savingMeta ? $t('admin.games.saving_meta') : $t('admin.games.save_meta') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Config panels -->
    <div v-if="selectedGame === 'dice'">
      <DiceAdminConfig />
    </div>

    <div v-else-if="selectedGame === 'mines'">
      <MinesAdminConfig />
    </div>

    <div v-else-if="selectedGame === 'crash'">
      <CrashAdminConfig />
    </div>

    <div v-else-if="selectedGame === 'plinko'">
      <PlinkoAdminConfig />
    </div>

    <div v-else-if="selectedGame === 'keno'">
      <KenoAdminConfig />
    </div>

    <div v-else-if="selectedGame === 'hilo'">
      <HiloAdminConfig />
    </div>

    <div v-else-if="selectedGame === 'blackjack'">
      <BlackjackAdminConfig />
    </div>

    <div v-else-if="selectedGame === 'roulette'">
      <RouletteAdminConfig />
    </div>

    <div v-else-if="selectedGame === 'baccarat'">
      <BaccaratAdminConfig />
    </div>

    <div v-else-if="selectedGame === 'video-poker'">
      <VideoPokerAdminConfig />
    </div>

    <div v-else-if="selectedGame === 'slots'">
      <SlotAdminConfig />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'
import BaccaratAdminConfig from '@/components/admin/BaccaratAdminConfig.vue'
import BlackjackAdminConfig from '@/components/admin/BlackjackAdminConfig.vue'
import CrashAdminConfig from '@/components/admin/CrashAdminConfig.vue'
import DiceAdminConfig from '@/components/admin/DiceAdminConfig.vue'
import HiloAdminConfig from '@/components/admin/HiloAdminConfig.vue'
import KenoAdminConfig from '@/components/admin/KenoAdminConfig.vue'
import MinesAdminConfig from '@/components/admin/MinesAdminConfig.vue'
import PlinkoAdminConfig from '@/components/admin/PlinkoAdminConfig.vue'
import RouletteAdminConfig from '@/components/admin/RouletteAdminConfig.vue'
import SlotAdminConfig from '@/components/admin/SlotAdminConfig.vue'
import VideoPokerAdminConfig from '@/components/admin/VideoPokerAdminConfig.vue'

const { t } = useI18n()
const toast = useToastStore()

interface GameState {
  id: number | null
  slug: string
  is_enabled: boolean
  is_hot: boolean
  is_new: boolean
  display_order: number
  category: string
}

const games = computed(() => [
  { id: 'dice', label: t('admin.games.game_dice') },
  { id: 'mines', label: t('admin.games.game_mines') },
  { id: 'crash', label: t('admin.games.game_crash') },
  { id: 'plinko', label: t('admin.games.game_plinko') },
  { id: 'keno', label: t('admin.games.game_keno') },
  { id: 'hilo', label: t('admin.games.game_hilo') },
  { id: 'blackjack', label: t('admin.games.game_blackjack') },
  { id: 'roulette', label: t('admin.games.game_roulette') },
  { id: 'baccarat', label: t('admin.games.game_baccarat') },
  { id: 'video-poker', label: t('admin.games.game_video_poker') },
  { id: 'slots', label: t('admin.games.game_slots') },
])

const selectedGame = ref('plinko')
const toggling = ref(false)
const savingMeta = ref(false)
const metaOpen = ref(false)

// Per-slug game state map, populated from API responses
const gameStates = reactive<Record<string, GameState>>({})

// Metadata form — reset when switching games
const metaForm = reactive({
  title: '',
  description: '',
  rtp: 0,
  rules_url: '',
  thumbnail_url: '',
})

const selectedGameLabel = computed(
  () => games.value.find((g) => g.id === selectedGame.value)?.label ?? selectedGame.value,
)

function defaultState(slug: string): GameState {
  return {
    id: null,
    slug,
    is_enabled: true,
    is_hot: false,
    is_new: false,
    display_order: 0,
    category: '',
  }
}

// Ensure game state exists for a slug (call outside computed to avoid side-effect lint)
function ensureState(slug: string): void {
  if (!gameStates[slug]) {
    gameStates[slug] = defaultState(slug)
  }
}

// Initialize state for all games upfront
games.value.forEach((g) => ensureState(g.id))

const currentState = computed((): GameState => {
  return gameStates[selectedGame.value]
})

function applyGameResponse(data: {
  id: number
  slug: string
  title: string
  category: string
  description: string | null
  rules_url: string | null
  is_enabled: boolean
  is_hot: boolean
  is_new: boolean
  rtp: number | null
  thumbnail_url: string | null
  display_order: number
  play_count: number
}) {
  gameStates[data.slug] = {
    id: data.id,
    slug: data.slug,
    is_enabled: data.is_enabled,
    is_hot: data.is_hot,
    is_new: data.is_new,
    display_order: data.display_order,
    category: data.category ?? '',
  }
  // Only update meta form if this is the currently selected game
  if (data.slug === selectedGame.value) {
    metaForm.title = data.title ?? ''
    metaForm.description = data.description ?? ''
    metaForm.rtp = data.rtp ?? 0
    metaForm.rules_url = data.rules_url ?? ''
    metaForm.thumbnail_url = data.thumbnail_url ?? ''
  }
}

async function toggleEnabled() {
  const state = currentState.value
  if (state.id === null) {
    toast.error(t('admin.games.toast_not_loaded'))
    return
  }
  toggling.value = true
  try {
    const res = await apiClient.post<{ message: string; game: Record<string, unknown> }>(
      `/admin/api/games/${state.id}/toggle`,
    )
    applyGameResponse(res.data.game as Parameters<typeof applyGameResponse>[0])
    toast.success(res.data.message)
  } catch {
    toast.error(t('admin.games.toast_toggle_failed'))
  } finally {
    toggling.value = false
  }
}

async function saveOrder() {
  const state = currentState.value
  if (state.id === null) return
  try {
    const res = await apiClient.put<{ game: Record<string, unknown> }>(
      `/admin/api/games/${state.id}/order`,
      { display_order: state.display_order },
    )
    applyGameResponse(res.data.game as Parameters<typeof applyGameResponse>[0])
    toast.success(t('admin.games.toast_order_updated'))
  } catch {
    toast.error(t('admin.games.toast_order_failed'))
  }
}

async function saveCategory() {
  const state = currentState.value
  if (state.id === null) return
  try {
    const res = await apiClient.put<{ game: Record<string, unknown> }>(
      `/admin/api/games/${state.id}/category`,
      { category: state.category },
    )
    applyGameResponse(res.data.game as Parameters<typeof applyGameResponse>[0])
    toast.success(t('admin.games.toast_category_updated'))
  } catch {
    toast.error(t('admin.games.toast_category_failed'))
  }
}

async function saveMetadata() {
  const state = currentState.value
  if (state.id === null) {
    toast.error(t('admin.games.toast_not_loaded'))
    return
  }
  savingMeta.value = true
  try {
    const res = await apiClient.put<{ game: Record<string, unknown> }>(
      `/admin/api/games/${state.id}/metadata`,
      {
        title: metaForm.title || undefined,
        description: metaForm.description || undefined,
        rtp: metaForm.rtp || undefined,
        rules_url: metaForm.rules_url || undefined,
        thumbnail_url: metaForm.thumbnail_url || undefined,
      },
    )
    applyGameResponse(res.data.game as Parameters<typeof applyGameResponse>[0])
    toast.success(t('admin.games.toast_meta_saved'))
  } catch {
    toast.error(t('admin.games.toast_meta_failed'))
  } finally {
    savingMeta.value = false
  }
}

// Fetch game info for selected game via a lightweight toggle preflight using GET on /admin/api/games list
// Since there is no list endpoint, we bootstrap by POSTing toggle and immediately toggling back — that's
// wrong. Instead, we use the first actual user action to populate state.
// To pre-populate without a list endpoint, we skip auto-fetch; id=null guards all write actions.
// When the user navigates to a game tab, reset metaForm to blank so stale data isn't shown.
watch(selectedGame, () => {
  metaOpen.value = false
  const existing = gameStates[selectedGame.value]
  if (existing && existing.id !== null) {
    // state already populated — nothing to do; metaForm stays consistent via applyGameResponse
  } else {
    metaForm.title = ''
    metaForm.description = ''
    metaForm.rtp = 0
    metaForm.rules_url = ''
    metaForm.thumbnail_url = ''
  }
})
</script>

<style scoped>
.games-page {
  padding: 24px;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 24px;
}

/* Tab bar */
.game-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.game-tab {
  padding: 8px 18px;
  border-radius: 6px;
  border: 1px solid var(--border, #2d4a5a);
  background: var(--bg-secondary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s;
}

.game-tab.active {
  background: var(--accent, #1a9e6e);
  border-color: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}

.game-tab:hover:not(.active) {
  color: var(--text-primary, #fff);
  border-color: var(--text-secondary, #b1bad3);
}

/* Overview bar */
.game-overview-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 14px 20px;
  margin-bottom: 12px;
}

.overview-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.overview-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.overview-slug {
  font-size: 0.75rem;
  color: var(--text-tertiary, #7f8fa6);
  font-family: monospace;
  background: var(--bg-primary, #0f212e);
  padding: 2px 7px;
  border-radius: 4px;
}

.status-badge {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.badge-enabled {
  background: rgba(26, 158, 110, 0.2);
  color: var(--color-success-light, #34d399);
}

.badge-disabled {
  background: rgba(239, 68, 68, 0.15);
  color: var(--color-danger-light, #f87171);
}

.badge-hot {
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
}

.badge-new {
  background: rgba(59, 130, 246, 0.2);
  color: var(--color-info-light, #60a5fa);
}

.overview-right {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

/* Order input */
.order-label,
.category-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: var(--text-secondary, #b1bad3);
}

.order-input {
  width: 64px;
  padding: 6px 8px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
  text-align: center;
}

.category-select {
  padding: 6px 10px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
}

/* Custom toggle switch */
.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-text {
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  min-width: 54px;
}

.toggle-switch {
  position: relative;
  width: 42px;
  height: 24px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
  padding: 0;
}

.toggle-switch.toggle-on {
  background: var(--accent, #1a9e6e);
  border-color: var(--accent, #1a9e6e);
}

.toggle-switch:disabled {
  opacity: 0.5;
  cursor: default;
}

.toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  background: var(--text-secondary, #b1bad3);
  border-radius: 50%;
  transition: transform 0.2s, background 0.2s;
}

.toggle-on .toggle-knob {
  transform: translateX(18px);
  background: #fff;
}

/* Metadata section */
.metadata-section {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  margin-bottom: 20px;
  overflow: hidden;
}

.metadata-toggle-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: none;
  border: none;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}

.metadata-toggle-btn:hover {
  background: var(--bg-tertiary, #1a2f3d);
}

.chevron {
  font-size: 1rem;
  transition: transform 0.2s;
  display: inline-block;
}

.chevron.open {
  transform: rotate(180deg);
}

.metadata-body {
  padding: 0 20px 20px;
  border-top: 1px solid var(--border, #2d4a5a);
}

.meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 16px;
}

.meta-full {
  grid-column: 1 / -1;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
}

.form-input,
.form-textarea {
  padding: 8px 12px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
}

.form-textarea {
  resize: vertical;
  font-family: inherit;
}

.meta-actions {
  margin-top: 16px;
  display: flex;
  gap: 12px;
}

/* Buttons */
.btn {
  padding: 9px 20px;
  border-radius: 7px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-primary {
  background: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}
</style>
